import { type BasketItem } from "../types";

import { useKioskSession } from "./use-kiosk-session";

/** Total quantity of a product in the basket, summed across all its lines. */
export function getKioskProductQuantity(
  basket: BasketItem[],
  productId: string
): number {
  return basket.reduce(
    (total, item) =>
      item.productId === productId ? total + item.quantity : total,
    0
  );
}

export function useKioskProductQuantity(productId: string): number {
  return useKioskSession((session) =>
    getKioskProductQuantity(session?.basket ?? [], productId)
  );
}

/**
 * The basket line id of a product's regular (non-variant/weight) line, if any.
 * Used to increment/decrement a simple product straight from the card.
 */
export function useKioskProductRegularLineId(
  productId: string
): string | undefined {
  return useKioskSession(
    (session) =>
      (session?.basket ?? []).find(
        (item) => item.productId === productId && item.type === "regular"
      )?.id
  );
}

/**
 * The basket line id of the most recently added line for a product (any type).
 * Used to remove the last addition of products that are otherwise managed
 * through the detail modal (variants, weighted, discounted).
 */
export function useKioskProductLastLineId(
  productId: string
): string | undefined {
  return useKioskSession((session) => {
    const lines = (session?.basket ?? []).filter(
      (item) => item.productId === productId
    );

    return lines[lines.length - 1]?.id;
  });
}
